// Six visual styles for the Skills section. Each accepts:
//   skills:    array of {id?, name, level? (0-100)}
//   accent:    CSS color string
//   compact:   bool — slightly smaller / tighter
//
// `level` defaults to 80 for proficiency-style visuals.

import React from "react";

const norm = (s) => (s || []).map(x => ({
  name: (typeof x === "string" ? x : x?.name) || "",
  level: typeof x?.level === "number" ? x.level : 80,
})).filter(x => x.name);

export function SkillBullets({ skills, accent }) {
  const list = norm(skills);
  return (
    <ul className="r-skill-bullets">
      {list.map((s, i) => (
        <li key={i}><span style={{ color: accent }}>▪</span> {s.name}</li>
      ))}
    </ul>
  );
}

export function SkillTags({ skills, accent }) {
  const list = norm(skills);
  return (
    <div className="r-skill-tags">
      {list.map((s, i) => (
        <span key={i} className="r-skill-tag" style={{ borderColor: accent, color: accent }}>{s.name}</span>
      ))}
    </div>
  );
}

export function SkillBars({ skills, accent }) {
  const list = norm(skills);
  return (
    <div className="r-skill-bars">
      {list.map((s, i) => (
        <div key={i} className="r-skill-bar-row">
          <span className="r-skill-bar-label">{s.name}</span>
          <div className="r-skill-bar"><div className="r-skill-bar-fill" style={{ width: `${s.level}%`, background: accent }} /></div>
        </div>
      ))}
    </div>
  );
}

export function SkillDots({ skills, accent }) {
  const list = norm(skills);
  return (
    <div className="r-skill-dots">
      {list.map((s, i) => {
        const filled = Math.round((s.level / 100) * 5);
        return (
          <div key={i} className="r-skill-dot-row">
            <span className="r-skill-dot-label">{s.name}</span>
            <span className="r-skill-dot-track">
              {[0,1,2,3,4].map(n => (
                <span key={n} className="r-skill-dot" style={{ background: n < filled ? accent : "#E5E7EB" }} />
              ))}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export function SkillPercent({ skills, accent }) {
  const list = norm(skills);
  return (
    <div className="r-skill-pct">
      {list.map((s, i) => (
        <div key={i} className="r-skill-pct-row">
          <span className="r-skill-pct-name">{s.name}</span>
          <span className="r-skill-pct-val" style={{ color: accent }}>{s.level}%</span>
        </div>
      ))}
    </div>
  );
}

export function SkillStars({ skills, accent }) {
  const list = norm(skills);
  return (
    <div className="r-skill-stars">
      {list.map((s, i) => {
        const filled = Math.round((s.level / 100) * 5);
        return (
          <div key={i} className="r-skill-star-row">
            <span className="r-skill-star-label">{s.name}</span>
            <span className="r-skill-star-track">
              {[0,1,2,3,4].map(n => (
                <span key={n} className="r-skill-star" style={{ color: n < filled ? accent : "#E5E7EB" }}>★</span>
              ))}
            </span>
          </div>
        );
      })}
    </div>
  );
}

const STYLES = {
  bullets: SkillBullets,
  tags:    SkillTags,
  bars:    SkillBars,
  dots:    SkillDots,
  percent: SkillPercent,
  stars:   SkillStars,
};

export default function Skills({ skills, accent, style = "tags" }) {
  const Component = STYLES[style] || SkillTags;
  return <Component skills={skills} accent={accent} />;
}
